import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import toColorString from 'polished/lib/color/toColorString';
import { color } from '@decisiv/design-tokens';

import { P } from '@decisiv/ui-components/lib/components/Typography';
import ChevronDown from '@decisiv/iconix/lib/components/ChevronDown';
import ChevronUp from '@decisiv/iconix/lib/components/ChevronUp';

import EventRow from './EventRow';
import { ListContainer, ExpandBar } from './styledComponents';

const WINDOW_SIZE = 10;

function EventList({ events, anchorId, errorIds }) {
  const anchorIndex = useMemo(() => {
    const idx = events.findIndex((e) => e['@id'] === anchorId);
    return idx >= 0 ? idx : 0;
  }, [events, anchorId]);

  const initialStart = Math.max(0, anchorIndex - WINDOW_SIZE);
  const initialEnd = Math.min(events.length, anchorIndex + WINDOW_SIZE + 1);

  const [visibleStart, setVisibleStart] = useState(initialStart);
  const [visibleEnd, setVisibleEnd] = useState(initialEnd);
  const anchorRef = useRef(null);

  useEffect(() => {
    setVisibleStart(initialStart);
    setVisibleEnd(initialEnd);
  }, [initialStart, initialEnd]);

  useEffect(() => {
    if (anchorRef.current) {
      const timer = setTimeout(() => {
        anchorRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [events, anchorId]);

  const hiddenBefore = visibleStart;
  const hiddenAfter = events.length - visibleEnd;

  const handleShowEarlier = useCallback(() => {
    setVisibleStart(0);
  }, []);

  const handleShowLater = useCallback(() => {
    setVisibleEnd(events.length);
  }, [events.length]);

  const visibleEvents = events.slice(visibleStart, visibleEnd);

  return (
    <ListContainer>
      {hiddenBefore > 0 && (
        <ExpandBar
          justifyContent="center"
          alignItems="center"
          paddingY={0.5}
          onClick={handleShowEarlier}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              if (e.key === ' ') e.preventDefault();
              handleShowEarlier();
            }
          }}
        >
          <ChevronUp
            size="small"
            color={toColorString(color.interaction.pacificOcean)}
          />
          <P shade={1} marginLeft={0.5}>
            Show {hiddenBefore} earlier{' '}
            {hiddenBefore === 1 ? 'event' : 'events'}
          </P>
        </ExpandBar>
      )}

      {visibleEvents.map((event, i) => {
        const eventId = event['@id'];
        const isAnchor = eventId === anchorId;

        return (
          <div
            key={eventId || visibleStart + i}
            ref={isAnchor ? anchorRef : undefined}
          >
            <EventRow event={event} isAnchor={isAnchor} />
          </div>
        );
      })}

      {hiddenAfter > 0 && (
        <ExpandBar
          justifyContent="center"
          alignItems="center"
          paddingY={0.5}
          onClick={handleShowLater}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              if (e.key === ' ') e.preventDefault();
              handleShowLater();
            }
          }}
        >
          <ChevronDown
            size="small"
            color={toColorString(color.interaction.pacificOcean)}
          />
          <P shade={1} marginLeft={0.5}>
            Show {hiddenAfter} later {hiddenAfter === 1 ? 'event' : 'events'}
          </P>
        </ExpandBar>
      )}
    </ListContainer>
  );
}

EventList.propTypes = {
  events: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  anchorId: PropTypes.string,
  errorIds: PropTypes.arrayOf(PropTypes.string),
};

EventList.defaultProps = {
  anchorId: null,
  errorIds: [],
};

export default EventList;
